import { Link } from 'react-router-dom';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { useApi } from '../../lib/useApi.js';
import { useClientPagination } from '../../lib/usePagination.js';
import {
  PageHeader,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  StatusChip,
  PageViewerControls,
} from '../../components/primitives.jsx';
import {
  KpiHeroBand,
  StatusDonutMini,
  WorkflowPipeline,
  buildPipelineSteps,
  StaggerReveal,
} from '../../components/index.js';
import { GlossaryTerm } from '../../components/primitives/GlossaryTerm.jsx';
import './access.redesign.css';

// Canonical eSAAR chain rendered as a horizontal pipeline. Terminal/branch
// states (denied, returned, withdrawn, expired, revoked) are intentionally
// excluded from the linear ribbon and surfaced as a forced "blocked" tone on
// the stage where the request stalled (see deriveChain below).
const CHAIN_ORDER = [
  'draft',
  'submitted',
  'supervisor_review',
  'security_review',
  'portfolio_owner_review',
  'data_steward_review',
  'admin_provisioning',
  'provisioned',
  'active',
];

const CHAIN_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  supervisor_review: 'Supervisor',
  security_review: 'Security',
  portfolio_owner_review: 'Portfolio',
  data_steward_review: 'Steward',
  admin_provisioning: 'Provisioning',
  provisioned: 'Provisioned',
  active: 'Active',
};

// Buckets used for the posture band and donut.
const OPEN_STATES = new Set([
  'draft',
  'submitted',
  'supervisor_review',
  'security_review',
  'portfolio_owner_review',
  'data_steward_review',
  'admin_provisioning',
  'returned_for_rework',
  'recertification_due',
]);
const APPROVED_STATES = new Set(['provisioned', 'active']);
const DENIED_STATES = new Set(['denied', 'withdrawn', 'expired', 'revoked']);

function postureOf(state) {
  if (APPROVED_STATES.has(state)) return 'approved';
  if (DENIED_STATES.has(state)) return 'denied';
  return 'open';
}

const RISK_TONE = { standard: 'sage', elevated: 'amber', high: 'brick' };

// States where the requester can fix and resubmit their own request.
const EDITABLE_STATES = new Set(['draft', 'returned_for_rework']);

// The reviewer's most recent return note (why the request came back).
function returnNote(req) {
  const returned = (req.workflowSteps || []).filter((s) => s.status === 'returned');
  return returned.length ? returned[returned.length - 1].note || null : null;
}

function freshness(req) {
  const raw = req.lastUpdatedAt || req.submittedAt || req.workflowSteps?.at(-1)?.completedAt;
  if (!raw) return null;
  const then = new Date(raw).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.max(0, Math.round((Date.now() - then) / 86400000));
  const label =
    days === 0 ? 'Updated today' : days === 1 ? 'Updated 1d ago' : `Updated ${days}d ago`;
  // Stale work that sits open too long is a process-risk signal.
  const tone = days >= 14 ? 'brick' : days >= 7 ? 'amber' : 'slate';
  return { days, label, tone };
}

// Find the last meaningful step to decide whether a request is blocked, and at
// which logical stage it stalled. Denied / returned-for-rework preserve a
// distinct "blocked" tone instead of collapsing to a neutral pending stage.
function deriveChain(req) {
  const current = req.currentWorkflowState || req.workflowSteps?.at(-1)?.workflowState || 'draft';
  const statusByKey = {};
  if (current === 'denied') {
    // Mark the furthest review stage reached as the block point.
    const reached = (req.workflowSteps || [])
      .map((s) => s.workflowState)
      .filter((w) => CHAIN_ORDER.includes(w));
    const blockKey = reached.at(-1) || 'submitted';
    statusByKey[blockKey] = 'blocked';
    return buildPipelineSteps({
      order: CHAIN_ORDER,
      current: blockKey,
      statusByKey,
      labels: CHAIN_LABELS,
    });
  }
  if (current === 'returned_for_rework') {
    statusByKey.draft = 'blocked';
    return buildPipelineSteps({
      order: CHAIN_ORDER,
      current: 'draft',
      statusByKey,
      labels: CHAIN_LABELS,
    });
  }
  if (DENIED_STATES.has(current)) {
    // withdrawn / expired / revoked — show as blocked on the latest known stage.
    const reached = (req.workflowSteps || [])
      .map((s) => s.workflowState)
      .filter((w) => CHAIN_ORDER.includes(w));
    const blockKey = reached.at(-1) || 'draft';
    statusByKey[blockKey] = 'blocked';
    return buildPipelineSteps({
      order: CHAIN_ORDER,
      current: blockKey,
      statusByKey,
      labels: CHAIN_LABELS,
    });
  }
  return buildPipelineSteps({ order: CHAIN_ORDER, current, labels: CHAIN_LABELS });
}

export function AccessStatusPage() {
  const { user } = useCurrentUser();
  const r = useApi('/access-requests', {
    query: { requesterUserId: user?.id || '' },
    skip: !user?.id,
  });
  const items = r.data?.data || [];
  const requestPager = useClientPagination(items, {
    initialPageSize: 5,
    resetKey: user?.id || 'anonymous',
  });
  if (!user)
    return (
      <EmptyState
        title="Not signed in"
        action={
          <Link className="btn primary" to="/login">
            Pick a persona →
          </Link>
        }
      />
    );
  if (r.loading)
    return (
      <>
        <PageHeader title="Access status" />
        <LoadingSkeleton rows={6} />
      </>
    );
  if (r.error) return <ErrorState error={r.error} />;

  // Posture rollup over every request (not just the current page).
  const counts = { open: 0, approved: 0, denied: 0 };
  for (const req of items) {
    counts[postureOf(req.currentWorkflowState)] += 1;
  }
  // Pipeline-stage distribution across open requests — where work is sitting.
  const stageBuckets = new Map();
  for (const req of items) {
    const state = req.currentWorkflowState || 'draft';
    if (!OPEN_STATES.has(state)) continue;
    const key = state === 'returned_for_rework' ? 'draft' : state;
    const label = CHAIN_LABELS[key] || 'In review';
    stageBuckets.set(label, (stageBuckets.get(label) || 0) + 1);
  }
  const STAGE_TONE_CYCLE = ['blue', 'amber', 'sage', 'slate'];
  const distribution = [...stageBuckets.entries()].map(([label, count], i) => ({
    label,
    count,
    tone: STAGE_TONE_CYCLE[i % STAGE_TONE_CYCLE.length],
  }));
  const distTotal = distribution.reduce((a, s) => a + s.count, 0);

  const kpiItems = [
    {
      id: 'open',
      label: 'Open',
      value: counts.open,
      icon: '⏳',
      status: counts.open > 0 ? 'amber' : 'slate',
      statusLabel: counts.open > 0 ? 'In flight' : 'Clear',
      footer: 'Awaiting a review decision',
    },
    {
      id: 'approved',
      label: 'Approved',
      value: counts.approved,
      icon: '✓',
      status: 'sage',
      statusLabel: counts.approved > 0 ? 'Provisioned' : undefined,
      footer: 'Provisioned or active access',
    },
    {
      id: 'denied',
      label: 'Denied / closed',
      value: counts.denied,
      icon: '⊘',
      status: counts.denied > 0 ? 'brick' : 'slate',
      statusLabel: counts.denied > 0 ? 'Closed' : undefined,
      footer: 'Denied, withdrawn, expired, or revoked',
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow={<GlossaryTerm term="eSAAR" />}
        title="My access"
        subtitle="Current and historical access requests."
        actions={
          <Link className="btn primary" to="/access/request">
            New request
          </Link>
        }
      />
      {items.length === 0 ? (
        <EmptyState title="No access requests" description="Submit one from the action above." />
      ) : (
        <div className="access-page">
          <KpiHeroBand eyebrow="Access posture" items={kpiItems} columns={3} />

          {distTotal > 0 ? (
            <section className="card">
              <h2 className="card-title">Open requests by stage</h2>
              <div className="access-posture">
                <StatusDonutMini
                  segments={distribution}
                  size={104}
                  totalLabel="Open"
                  ariaLabel="Open access requests by pipeline stage"
                />
                <div className="access-distribution">
                  <div
                    className="access-distribution__bar"
                    role="img"
                    aria-label={`Pipeline stage distribution: ${distribution
                      .map((s) => `${s.label} ${s.count}`)
                      .join(', ')}`}
                  >
                    {distribution.map((s) => (
                      <span
                        key={s.label}
                        className={`access-distribution__seg tone-${s.tone}`}
                        style={{ flex: `${s.count} 0 0` }}
                        title={`${s.label}: ${s.count}`}
                      />
                    ))}
                  </div>
                  <ul className="access-distribution__legend">
                    {distribution.map((s) => (
                      <li key={s.label}>
                        <span
                          className={`access-distribution__swatch access-distribution__seg tone-${s.tone}`}
                        />
                        {s.label}
                        <span className="access-distribution__count">{s.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
          ) : null}

          <StaggerReveal className="access-request-list">
            {requestPager.items.map((req) => {
              const risk = req.riskLevel || 'standard';
              const riskTone = RISK_TONE[risk] || 'slate';
              const fresh = freshness(req);
              const steps = deriveChain(req);
              const state = req.currentWorkflowState || 'draft';
              const canResubmit = EDITABLE_STATES.has(state);
              const note = state === 'returned_for_rework' ? returnNote(req) : null;
              return (
                <div key={req.id} className="card access-req-card">
                  <div className="access-req-card__head">
                    <div className="access-req-card__idblock">
                      <strong className="access-req-card__id">{req.id}</strong>
                      <div className="access-chip-row">
                        <span
                          className={`access-chip tone-${riskTone}`}
                          title={`Risk level: ${risk}`}
                        >
                          <span className="access-chip__dot" />
                          <span className="access-chip__label">Risk</span>
                          <span style={{ textTransform: 'capitalize' }}>{risk}</span>
                        </span>
                        {fresh ? (
                          <span className={`access-chip tone-${fresh.tone}`} title={fresh.label}>
                            <span className="access-chip__dot" />
                            {fresh.label}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <StatusChip status={req.currentWorkflowState} />
                  </div>
                  {req.justification ? (
                    <p className="access-req-card__justification">{req.justification}</p>
                  ) : null}
                  {note ? (
                    <div className="access-return-note" role="note">
                      <span className="access-return-note__label">Returned for rework</span>
                      <span className="access-return-note__text">{note}</span>
                    </div>
                  ) : null}
                  <div className="access-pipeline-block">
                    <WorkflowPipeline
                      steps={steps}
                      ariaLabel={`eSAAR approval chain for ${req.id}`}
                    />
                  </div>
                  {canResubmit ? (
                    <div className="access-req-card__actions">
                      <Link className="btn primary" to={`/access/request/${req.id}`}>
                        Fix &amp; resubmit →
                      </Link>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </StaggerReveal>
          <PageViewerControls
            page={requestPager.page}
            label="access requests"
            pageSize={requestPager.pageSize}
            onOffsetChange={requestPager.setOffset}
            onPageSizeChange={requestPager.setPageSize}
          />
        </div>
      )}
    </>
  );
}
